// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PROJECT_STATUS_LABELS } from '@/constants/project-status';
import {
  CREATE_PROJECT_OPTION_ID,
  ProjectAutocomplete,
} from '@/project/components/project-autocomplete';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { SparseProject } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';
import { reject } from 'lodash-es';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Valid grouping methods for project search results.
 * Can be combined in an array to create hierarchical grouping.
 *
 * - 'active': Groups active projects
 * - 'past': Groups past projects
 * - 'upcoming': Groups upcoming projects
 * - 'date-range-incomplete': Groups projects without dates set
 * - 'customer': Groups by customer name (or 'No customer' if no customer)
 * - 'non-customer': Groups by presence/absence of customer
 * - 'none': No grouping applied
 *
 * Note: Cannot mix multiple status methods (e.g., 'active' + 'past').
 * Note: 'customer' and 'non-customer' are mutually exclusive.
 */
export type ProjectGroupingMethod =
  | 'active'
  | 'past'
  | 'upcoming'
  | 'date-range-incomplete'
  | 'customer'
  | 'non-customer'
  | 'none';

export type ProjectFilterCallback = (
  options: SparseProject[]
) => SparseProject[];

export type ProjectSearchValue<Multiple> = Multiple extends true
  ? SparseProject[]
  : SparseProject | null;

const filter = createFilterOptions<SparseProject>();

/**
 * Validates that the grouping array doesn't contain mutually exclusive methods.
 * Throws an error if validation fails.
 *
 * @param grouping - The array of grouping methods to validate
 * @throws Error if conflicting grouping methods are present
 */
function validateGrouping(grouping: ProjectGroupingMethod[] | undefined) {
  if (!grouping) {
    return;
  }

  const statusMethods = ['active', 'past', 'upcoming', 'date-range-incomplete'];
  const statusMethodsInGrouping = grouping.filter((method) =>
    statusMethods.includes(method)
  );

  // Check if multiple status methods are used
  if (statusMethodsInGrouping.length > 1) {
    throw new Error(
      `ProjectSearch: Cannot use multiple status grouping methods together (${statusMethodsInGrouping.join(', ')})`
    );
  }

  const hasCustomer = grouping.includes('customer');
  const hasNonCustomer = grouping.includes('non-customer');

  if (hasCustomer && hasNonCustomer) {
    throw new Error(
      "ProjectSearch: 'customer' and 'non-customer' grouping methods are mutually exclusive"
    );
  }
}

/**
 * Creates a grouping function for project search results based on the specified grouping methods.
 * Supports hierarchical grouping by combining multiple methods.
 *
 * @param grouping - Array of grouping methods to apply, evaluated in order
 * @returns A function that determines the group label for each project, or undefined if no grouping
 *
 * @example
 * // Single grouping
 * createGroupBy(['active']) // Returns "Active", "Past", etc.
 *
 * @example
 * // Hierarchical grouping
 * createGroupBy(['active', 'customer']) // Returns "Active / Acme Corp" or "Past / No customer"
 */
function createGroupBy(grouping: ProjectGroupingMethod[] | undefined) {
  if (!grouping || grouping.length === 0 || grouping.includes('none')) {
    return undefined;
  }

  return (option: SparseProject): string => {
    const groupLabels: string[] = [];

    for (const method of grouping) {
      let label: string;

      switch (method) {
        case 'active':
        case 'past':
        case 'upcoming':
        case 'date-range-incomplete':
          // For status grouping methods, use the project's actual status
          label = PROJECT_STATUS_LABELS[option.status];
          break;

        case 'customer':
          label = option.customer?.name ?? 'No customer';
          break;

        case 'non-customer':
          label = option.customer === null ? 'No customer' : 'Customer';
          break;

        case 'none':
          continue;

        default: {
          // This should never happen due to TypeScript, but satisfies exhaustiveness checking
          const _exhaustive: never = method;
          throw new Error(`Unknown grouping method: ${_exhaustive}`);
        }
      }

      groupLabels.push(label);
    }

    return groupLabels.join(' / ');
  };
}

/**
 * Compares two projects to be sorted as search results. Projects are sorted by
 * name.
 *
 * @param project - The first project to compare.
 * @param other - The second project to compare.
 */
function compareProjects(project: SparseProject, other: SparseProject) {
  if (project.isArchived === other.isArchived) {
    return project.name.localeCompare(other.name);
  }

  return project.isArchived ? 1 : -1;
}

/**
 * Returns the project options based on the filter provided. If no filter is
 * provided, all projects are returned.
 *
 * @param projects - The list of projects to filter.
 * @param filter - The filter to apply to the projects.
 */
function getOptions(
  projects: SparseProject[] = [],
  filter: ProjectFilterCallback | undefined
) {
  if (filter === undefined) {
    return projects;
  }

  return filter(projects);
}

interface Props<Multiple extends boolean = false> {
  multiple?: Multiple;
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, this function will be used to filter the project options.
   */
  filter?: ProjectFilterCallback;

  /**
   * If provided, project options will be grouped by these methods in hierarchical order.
   * Multiple methods can be combined to create nested grouping.
   *
   * @example
   * grouping={['active']} // Groups by active status
   * grouping={['active', 'customer']} // Groups by active status, then customer within each status group
   */
  grouping?: ProjectGroupingMethod[];

  /**
   * The currently selected project.
   */
  value: ProjectSearchValue<Multiple>;

  /**
   * A callback fired when the selected project changes.
   */
  onChange: (value: ProjectSearchValue<Multiple> | null) => void;

  /**
   * Callback fired when the user chooses to create a new project.
   * Receives the entered project name as a parameter.
   */
  onCreate?: (name: string) => void;
}

/**
 * Renders a project autocomplete component with options from the remote API.
 * This function also forwards a ref to the autocomplete component.
 */
function ProjectSearchRenderer<Multiple extends boolean = false>(
  props: Props<Multiple>,
  ref: ForwardedRef<HTMLInputElement>
) {
  // Validate grouping configuration
  validateGrouping(props.grouping);

  const { projects: allProjects, isLoading } = useSparseProjectList();

  const groupBy = createGroupBy(props.grouping);

  // Sort projects with grouping support
  const projects = getOptions(allProjects, props.filter)
    .slice()
    .sort((project, other) => {
      // If grouping is enabled, sort by group first
      if (groupBy) {
        const projectGroup = groupBy(project);
        const otherGroup = groupBy(other);

        // Special sorting for customer grouping: customers alphabetically, "No customer" last
        if (props.grouping?.includes('customer')) {
          const isProjectNoCustomer = projectGroup.includes('No customer');
          const isOtherNoCustomer = otherGroup.includes('No customer');

          // If one is "No customer" and the other isn't, "No customer" goes last
          if (isProjectNoCustomer !== isOtherNoCustomer) {
            return isProjectNoCustomer ? 1 : -1;
          }
        }

        // Special sorting for non-customer grouping: "No customer" first, then "Customer"
        if (props.grouping?.includes('non-customer')) {
          const isProjectNoCustomer = projectGroup.includes('No customer');
          const isOtherNoCustomer = otherGroup.includes('No customer');

          // If one is "No customer" and the other isn't, "No customer" goes first
          if (isProjectNoCustomer !== isOtherNoCustomer) {
            return isProjectNoCustomer ? -1 : 1;
          }
        }

        // Status ordering: upcoming, active, date-range-incomplete, past
        const statusOrder = ['Upcoming', 'Active', 'No dates set', 'Past'];
        const projectStatusIndex = statusOrder.findIndex((status) =>
          projectGroup.includes(status)
        );
        const otherStatusIndex = statusOrder.findIndex((status) =>
          otherGroup.includes(status)
        );

        if (projectStatusIndex !== -1 && otherStatusIndex !== -1) {
          if (projectStatusIndex !== otherStatusIndex) {
            return projectStatusIndex - otherStatusIndex;
          }
        }

        const groupComparison = projectGroup.localeCompare(otherGroup);

        if (groupComparison !== 0) {
          return groupComparison;
        }
      }

      // Then sort within the group using default comparison
      return compareProjects(project, other);
    });

  const filterOptions = (
    options: SparseProject[],
    state: FilterOptionsState<SparseProject>
  ) => {
    const filtered = filter(options, state);
    const inputValue = state.inputValue.trim();

    // Hide archived projects when no search input
    if (inputValue === '') {
      return reject(filtered, 'isArchived');
    }

    // Add create new option if there's input and no exact match
    if (
      props.onCreate &&
      inputValue &&
      !filtered.some(
        (project) => project.name.toLowerCase() === inputValue.toLowerCase()
      )
    ) {
      filtered.push({
        id: CREATE_PROJECT_OPTION_ID,
        key: CREATE_PROJECT_OPTION_ID,
        name: inputValue,
        isArchived: false,
        status: 'DATE_RANGE_INCOMPLETE',
        startOn: null,
        endOn: null,
        customer: null,
        integrations: [],
        paymentTermLabel: null,
        purchaseOrderNumber: null,
      });
    }

    // Show archived projects when searching
    return filtered;
  };

  const handleChange = (
    _: unknown,
    value: ProjectSearchValue<Multiple> | string | null
  ) => {
    if (typeof value === 'string') {
      if (props.onCreate) {
        return props.onCreate(value);
      }
      return;
    }

    if (value === null) {
      return props.onChange(null);
    }

    // Handle single selection mode
    if (!props.multiple) {
      const singleValue = value as SparseProject;
      if (singleValue.id === CREATE_PROJECT_OPTION_ID && props.onCreate) {
        return props.onCreate(singleValue.name);
      }
      // Normal project selection in single mode
      return props.onChange(singleValue as ProjectSearchValue<Multiple>);
    }

    // Handle multiple selection mode
    if (props.multiple && Array.isArray(value)) {
      const createOption = value.find(
        (project) => project.id === CREATE_PROJECT_OPTION_ID
      );
      if (createOption && props.onCreate) {
        return props.onCreate(createOption.name);
      }
      // Normal project selection in multiple mode
      return props.onChange(value as ProjectSearchValue<Multiple>);
    }

    // Fallback for any other case
    props.onChange(value as ProjectSearchValue<Multiple>);
  };

  return (
    <ProjectAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={projects}
      groupBy={groupBy}
      loading={isLoading}
      onChange={handleChange}
      disabled={props.disabled}
      multiple={props.multiple}
      helperText={props.helperText}
      filterOptions={filterOptions}
      loadingText="Loading..."
      noOptionsText="No projects found"
    />
  );
}

/**
 * A controlled component for selecting a project from a list of options from
 * the remote API.
 */
export const ProjectSearch = forwardRef(ProjectSearchRenderer);
